import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { UserContext } from "../../../api/userContext";
import { getFavoriteEvents } from "../../../api/favoriteEvents";
import { getSelectedEvents } from "../../../api/events";
import { getDefaultImage } from "../../../utils/eventUtils";
import { truncateAtLastSpace } from "../../../utils/textUtils";
// Swiper slider
import SwiperCore, { Autoplay, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.css";
import "./favoritesCarousel.css";

SwiperCore.use([Autoplay, Navigation]);

/**
 * A horizontal carousel displaying the user's favorite events.
 * Only shown when user is logged in and has favorites.
 */
const FavoritesCarousel = () => {
  const { user } = useContext(UserContext);
  const [favoriteEvents, setFavoriteEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    const loadFavoriteEvents = async () => {
      if (!user) {
        if (isActive) setLoading(false);
        return;
      }

      try {
        const favorites = await getFavoriteEvents(user.uid);
        if (!isActive) return;
        if (favorites && favorites.length > 0) {
          const eventIds = favorites.map(fav => fav.eventId);
          const events = await getSelectedEvents(eventIds);
          if (!isActive) return;
          setFavoriteEvents(events || []);
        } else {
          if (isActive) setFavoriteEvents([]);
        }
      } catch (error) {
        console.error("Error loading favorite events:", error);
        if (isActive) setFavoriteEvents([]);
      } finally {
        if (isActive) setLoading(false);
      }
    };

    loadFavoriteEvents();

    return () => {
      isActive = false;
    };
  }, [user]);

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateToDetails = (id) => {
    scrollTop();
    window.location.href = process.env.PUBLIC_URL + "/event-details?eid=" + id;
  };

  const sliderConfig = {
    slidesPerView: 1,
    speed: 600,
    spaceBetween: 20,
    loop: false,
    navigation: {
      nextEl: ".favorites-next",
      prevEl: ".favorites-prev",
    },
    breakpoints: {
      480: { slidesPerView: 1 },
      768: { slidesPerView: 2 },
      992: { slidesPerView: 3 },
      1200: { slidesPerView: 4 },
    },
  };

  // Don't render if not logged in or no favorites
  if (!user || loading) return null;
  if (favoriteEvents.length === 0) return null;

  return (
    <div className="favorites-carousel-section">
      <div className="favorites-header">
        <h4>
          <i className="bi bi-heart-fill"></i> Mes favoris
        </h4>
        <Link to="/events" className="favorites-see-all" onClick={scrollTop}>
          Voir tous <i className="bi bi-arrow-right"></i>
        </Link>
      </div>

      <div className="favorites-carousel-wrapper">
        <Swiper {...sliderConfig} className="favorites-swiper">
          {favoriteEvents.map((event, index) => (
            <SwiperSlide key={event.id}>
              <div 
                className="favorite-card"
                onClick={() => navigateToDetails(event.id)}
              >
                <div className="favorite-card-image">
                  <img src={getDefaultImage(event)} alt={event.title} />
                  <div className="favorite-card-date">
                    <span className="date-day">{new Date(event.date).getDate()}</span>
                    <span className="date-month">
                      {new Date(event.date).toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="favorite-card-content">
                  <h5 className="favorite-card-title">
                    {event.title.length > 50 ? truncateAtLastSpace(event.title, 50) : event.title}
                  </h5>
                  <p className="favorite-card-location">
                    <i className="bi bi-geo-alt"></i>
                    {event.location?.cityName}
                  </p>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Navigation arrows */}
        <div className="favorites-nav favorites-prev">
          <i className="bi bi-chevron-left"></i>
        </div>
        <div className="favorites-nav favorites-next">
          <i className="bi bi-chevron-right"></i>
        </div>
      </div>
    </div>
  );
};

export default FavoritesCarousel;
